/**
 * @fileoverview Shared callable API contract for
 * `recordSuperSectionAttendance`, which records a Super Section student's
 * attendance from the Section page's join and recording clicks. Imported by
 * both the functions handler and the webapp client so the two sides cannot
 * drift in name, request shape, or response shape.
 *
 * A Super Section is a webinar with no section leader to mark attendance, and
 * its students reach it only through those buttons, so the click is the
 * attendance signal. Firestore rules let only staff write a student's
 * attendance documents, which is why the click goes through a callable. See
 * [#5174](https://github.com/CodeinPlace/cip/issues/5174).
 */
import {z} from 'zod';

import {FirestoreDocumentIdSchema} from '../firebase/types';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * `sections` functions codebase so deployment and invocation resolve to the
 * same function.
 */
export const RECORD_SUPER_SECTION_ATTENDANCE_FUNCTION_NAME =
  'recordSuperSectionAttendance';

/**
 * What the client sends. A `join` click records the meeting whose window is
 * open now, so it names no week; a `recording` click names the week whose
 * recording was opened.
 */
export const RecordSuperSectionAttendanceRequestSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({
      kind: z.literal('join'),
      /** The course the Super Section belongs to. */
      courseId: FirestoreDocumentIdSchema,
      /** The Super Section time slot whose join button was clicked. */
      superSectionId: FirestoreDocumentIdSchema,
    }),
    z.object({
      kind: z.literal('recording'),
      /** The course the Super Section belongs to. */
      courseId: FirestoreDocumentIdSchema,
      /** The Super Section time slot whose recording was opened. */
      superSectionId: FirestoreDocumentIdSchema,
      /** The 0-based course week the recording is of. */
      weekIndex: z.number().int().nonnegative(),
    }),
  ],
);

/** Parsed request. */
export type RecordSuperSectionAttendanceRequest = z.infer<
  typeof RecordSuperSectionAttendanceRequestSchema
>;

/**
 * Why a call recorded nothing. The client ignores these, since opening the
 * meeting must never depend on attendance succeeding; they exist for logs and
 * tests.
 */
export type RecordSuperSectionAttendanceRefusal =
  /** The week was already recorded for this student; nothing changed. */
  | 'already-recorded'
  /**
   * `superSectionId` is not one of the course's `superSectionIds`, is not a
   * Super Section of that course, or has no first meeting configured.
   */
  | 'unknown-super-section'
  /**
   * The caller is not a student of the course, or, for a `join`, is placed in
   * a small section rather than the Super Section.
   */
  | 'not-super-section-student'
  /** A `join` outside the join window of the nearest meeting. */
  | 'outside-window'
  /** A `recording` for a week before the Super Section's first meeting. */
  | 'unknown-week'
  /** A `recording` for a week whose meeting has not started yet. */
  | 'week-not-started';

/** What the callable returns. */
export type RecordSuperSectionAttendanceResponse =
  | {
      /** The week was newly recorded. */
      recorded: true;
      /** The 0-based course week it was recorded against. */
      weekIndex: number;
    }
  | {
      /** Nothing was written. */
      recorded: false;
      /**
       * The 0-based course week the call resolved to, or `null` when the
       * refusal came before a week could be determined.
       */
      weekIndex: number | null;
      /** Why. */
      reason: RecordSuperSectionAttendanceRefusal;
    };

/**
 * Path of a student's per-course attendance document: a map from 0-based
 * course week to whether they attended. Small-section joins write the same
 * document, so staff views read Super Section students like any other.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionAttendanceDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionAttendance`;
}

/**
 * Path of a student's per-course document mapping each attended 0-based
 * course week to the ids of the sections they attended it in.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionAttendanceIdsDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionAttendanceIds`;
}

/**
 * Path of a student's per-course document marking the 0-based course weeks
 * whose Super Section recording they opened. A sibling of the attendance
 * document with the same week-to-boolean shape, kept apart so that watching a
 * recording is never mistaken for attending live.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionRecordingAttendanceDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionRecordingAttendance`;
}

/**
 * Path of a student's per-course document mapping each 0-based course week in
 * `sectionRecordingAttendance` to the ids of the sections whose recording they
 * marked: a Super Section, or their own small section for a catch-up. The
 * credit rule caps only the small-section weeks.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionRecordingAttendanceIdsDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionRecordingAttendanceIds`;
}
